// src/utils/formatExpiration.js
// Compact expiration countdown formatter for the Farm marketplace.
// Outputs time-to-expiration in human readable compact units:
// M (months), W (weeks), D (days), H (hours), m (minutes).

/**
 * Parse a product/offer expiry field into a Date.
 * Supports Firestore Timestamps, ISO strings, and plain Date objects.
 * For animal products, also considers the seller-provided bestBefore field.
 * @param {*} item - product/offer document data
 * @returns {Date|null}
 */
export function extractExpiryDate(item) {
  if (!item) return null;
  const candidates = [
    item.expiry,
    item.expiresAt,
    item.expiryDate,
    item.sellUntil,
    item.bestBefore,
  ];
  for (const raw of candidates) {
    if (!raw) continue;
    // Firestore Timestamp
    if (typeof raw?.toDate === 'function') {
      const d = raw.toDate();
      if (!isNaN(d.getTime())) return d;
    }
    // String or Date
    const d = new Date(raw);
    if (!isNaN(d.getTime())) return d;
  }
  return null;
}

/**
 * Returns true if the product is expired based on its expiry fields.
 * Live animals without an expiry are never considered expired.
 * @param {*} item
 * @returns {boolean}
 */
export function isItemExpired(item) {
  if (!item) return false;
  // Live animals do not carry a food expiry date; they are never expired
  // by the marketplace's food-safety rules.
  if (item.productType === 'animal' && !item.expiry && !item.bestBefore) {
    return false;
  }
  const date = extractExpiryDate(item);
  if (!date) return false; // no expiry → not expired
  return date.getTime() <= Date.now();
}

/**
 * Format the remaining time until expiration in compact units.
 * Returns null when no expiration exists.
 * Returns the string "expired" (i18n key) when expired.
 *
 * Examples: "3M", "2W", "5D", "12H", "45m"
 *
 * @param {Date|null} expiryDate
 * @returns {{ key: string, count: number }|string|null}
 *   - Object { key, count } for i18n interpolation
 *   - "expired" string for expired items
 *   - null if no expiry
 */
export function formatExpirationCompact(expiryDate) {
  if (!expiryDate) return null;
  const diff = expiryDate.getTime() - Date.now();
  if (diff <= 0) return 'expired';

  const totalMinutes = Math.floor(diff / (1000 * 60));
  const totalHours = Math.floor(totalMinutes / 60);
  const totalDays = Math.floor(totalHours / 24);
  const totalWeeks = Math.floor(totalDays / 7);
  const totalMonths = Math.floor(totalDays / 30);

  if (totalMonths >= 1) return { key: 'expiration.months', count: totalMonths };
  if (totalWeeks >= 1) return { key: 'expiration.weeks', count: totalWeeks };
  if (totalDays >= 1) return { key: 'expiration.days', count: totalDays };
  if (totalHours >= 1) return { key: 'expiration.hours', count: totalHours };
  return { key: 'expiration.minutes', count: Math.max(1, totalMinutes) };
}

/**
 * Multi-unit expiration format showing the two largest non-zero units.
 * Examples: "2M 1W", "3W 2D", "5D 12H", "3H 45m"
 * Returns the string "expired" when expired, null when no expiry.
 * @param {Date|null} expiryDate
 * @returns {string|null}
 */
export function formatExpirationMulti(expiryDate) {
  if (!expiryDate) return null;
  const diff = expiryDate.getTime() - Date.now();
  if (diff <= 0) return 'expired';

  const totalMinutes = Math.floor(diff / (1000 * 60));
  const totalHours = Math.floor(totalMinutes / 60);
  const totalDays = Math.floor(totalHours / 24);

  const months = Math.floor(totalDays / 30);
  const weeks = Math.floor((totalDays % 30) / 7);
  const days = totalDays % 7;
  const hours = totalHours % 24;
  const minutes = totalMinutes % 60;

  const parts = [];
  if (months > 0) parts.push(`${months}M`);
  if (weeks > 0) parts.push(`${weeks}W`);
  if (days > 0) parts.push(`${days}D`);
  if (hours > 0 && months === 0) parts.push(`${hours}H`);
  if (minutes > 0 && months === 0 && weeks === 0) parts.push(`${minutes}m`);

  // Show at most 2 units for compactness
  return parts.length > 0 ? parts.slice(0, 2).join(' ') : '1m';
}

/**
 * Plain-text fallback (no i18n) – used when t() is not available.
 * @param {Date|null} expiryDate
 * @returns {string|null}
 */
export function formatExpirationText(expiryDate) {
  const result = formatExpirationCompact(expiryDate);
  if (!result) return null;
  if (result === 'expired') return 'Expired';
  const suffixes = {
    'expiration.months': 'M',
    'expiration.weeks': 'W',
    'expiration.days': 'D',
    'expiration.hours': 'H',
    'expiration.minutes': 'm',
  };
  return `${result.count}${suffixes[result.key] || ''}`;
}

/**
 * UI countdown formatter used in cards and headers.
 * Matches legacy display style such as "2w 3d" or "5h 30m".
 * @param {Date|null} expiryDate
 * @returns {string|null}
 */
export function formatCountdownDisplay(expiryDate) {
  if (!expiryDate) return null;

  const diff = expiryDate.getTime() - Date.now();
  if (diff <= 0) return 'Expired';

  const totalMinutes = Math.floor(diff / (1000 * 60));
  const totalHours = Math.floor(totalMinutes / 60);
  const totalDays = Math.floor(totalHours / 24);
  const totalWeeks = Math.floor(totalDays / 7);
  const months = Math.floor(totalDays / 30);

  if (months > 0) {
    const remainingDays = totalDays % 30;
    return remainingDays > 0 ? `${months}m ${remainingDays}d` : `${months}m`;
  }

  if (totalWeeks > 0) {
    const remainingDays = totalDays % 7;
    return remainingDays > 0 ? `${totalWeeks}w ${remainingDays}d` : `${totalWeeks}w`;
  }

  if (totalDays > 0) {
    const remainingHours = totalHours % 24;
    return remainingHours > 0 ? `${totalDays}d ${remainingHours}h` : `${totalDays}d`;
  }

  if (totalHours > 0) {
    const remainingMinutes = totalMinutes % 60;
    return remainingMinutes > 0 ? `${totalHours}h ${remainingMinutes}m` : `${totalHours}h`;
  }

  return `${Math.max(1, totalMinutes)}m`;
}
